import validator from 'validator';
import { Module, ObjectType } from '../../configurationProfilesApp/exclusionItemGrid/types';
import { getObjectTypeModuleValues } from '../../configurationProfilesApp/exclusionsPanel/types';
import _ from 'lodash';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import {checkThatExclusionWithWildcardAreSupported, ExclusionItemWildcardValidator} from '../../ui/gzForm/validators/exclusion.validator';
import {FormValidator, ValidationResult} from '../../ui/gzForm/formField/types/form-validator';

// General size limit for an exclusion.
const nonPathExclusionLengthLimit = 260;
// Size limit for CMD params exclusions (enforced by Windows).
const cmdParamsLengthLimit = 2047;
// Size limit for path exclusions (files, folders, processes). This value is the max limit for Linux.
const pathExclusionLengthLimit = 4096;
// Object types which are paths (e.g.: file paths, folder paths, processes).
const pathObjectTypes = [ObjectType.FILE, ObjectType.FOLDER, ObjectType.PROCESS];

/**
 * Get all available modules for an Object Type
 * @param objectType
 * @returns {Array<number>}
 */
export function getAllAvailableModulesByObjectType(objectType: ObjectType): Array<number> {
  return (getObjectTypeModuleValues(objectType) || []).filter(module => !module.disabled).map(module => module.value);
}

/**
 * Validate an excluded item's modules, regardless of its type.
 * @param objectType
 * @param modules
 */
export function checkExcludedItemModules(objectType: ObjectType, modules: string): boolean {
  let checkedModules = modules.split(',').map(Number);

  checkedModules.forEach((item, index) => {
    if (item === Module.ALL) {
      checkedModules = getAllAvailableModulesByObjectType(objectType);
    }
  });

  const modulesForType = getAllAvailableModulesByObjectType(objectType);

  return isEmpty(_.difference(checkedModules, modulesForType));
}

/**
 * Validate if the exclusion contains the correct path, type and modules
 *
 * @param path
 * @param type
 * @param modules
 */
export function checkExcludedItemHasValidTypeAndModules(path: string, type: ObjectType, modules: string): boolean {
  const checkedModules = modules.split(',').map(Number);
  const exclusionItem = {type: type, path: path, modules: checkedModules};
  const valid: ValidationResult = checkThatExclusionWithWildcardAreSupported(exclusionItem);
  return valid.valid;
}

/**
 * Validate an excluded item, regardless of its type.
 * @param objectType
 * @param value
 */
export function validateExcludedItem(objectType: ObjectType, value: string): boolean {
  if (
    (objectType === ObjectType.CMD_PARAMS || objectType === ObjectType.CMD_PARAMS_REGEX) &&
    value.length &&
    value.length <= cmdParamsLengthLimit
  ) {
    return !/^\s*$/.test(value);
  }

  if (pathObjectTypes.includes(objectType)) {
    if (value.length > pathExclusionLengthLimit) {
      return false;
    }
  } else if (value.length > nonPathExclusionLengthLimit) {
    return false;
  }

  switch (objectType) {
    case ObjectType.FILE:
    case ObjectType.PROCESS:
      return validatePath(value, true);

    case ObjectType.FOLDER:
      return validatePath(value, false);

    case ObjectType.FILE_HASH:
      return validateMultipleHashes(value);

    case ObjectType.IP_MASK:
      return validateIpWithMask(value);

    case ObjectType.FILE_EXTENSION:
      return validateExtension(value);

    case ObjectType.THREAT_NAME:
      return validateThreatName(value);

    case ObjectType.CERTIFICATE_HASH:
      return validateHash(value);

    default:
      return false;
  }
}

/**
 * Validate all types of paths.
 *
 * @param path
 * @param isFileOrProcess specify if the path parameter refers a file/process or folder path
 * @return boolean
 */
function validatePath(path: string, isFileOrProcess: boolean): boolean {

  if (containsInvalidWildcards(path)) {
    return false;
  }

  // replace wildcards for easier validation
  if (path.startsWith('**')) {
    if (path[2] === '\\') {
      path = path.replace('**', 'C:');
    } else if (path[2] === '/') {
      path = path.replace('**', '/root');
    }
  }

  path = path.replace(/\*/g, 'ExampleRenderedPath');
  path = path.replace(/\?/g, 'A');

  return (
    validateWindowsAbsolutePath(path, isFileOrProcess) ||
    validateWindowsSpecialPath(path, isFileOrProcess) ||
    validateUnixSpecialPath(path, isFileOrProcess) ||
    validateUnixAbsolutePath(path, isFileOrProcess) ||
    validateNetworkPath(path)
  );
}

/**
 * Check if a string contains invalid wildcards: ***, *?, ?*, [, or ].
 * Wildcards are also not allowed in Windows environment variable
 *
 * @param value
 * @return boolean
 */
function containsInvalidWildcards(value: string): boolean {
  return /(\*\*\*|\*\?|\?\*|[\[\]]|%[a-zA-Z]*[?*]+[a-zA-Z]*%)/g.test(value);
}

/**
 * Check if a string contains wildcards
 *
 * @param {string} value
 * @returns {boolean}
 */
export function containsWildcards(value: string): boolean {
  return (new RegExp("\\*|\\?", "g")).test(value);
}

/**
 * Validate Windows absolute paths.
 *
 * @param value
 * @param isFileOrProcess specify if the path parameter refers a file/process or folder path
 * @return boolean
 */
function validateWindowsAbsolutePath(value: string, isFileOrProcess: boolean): boolean {
  if (isFileOrProcess) {
    return /^([a-zA-Z]:)(\\[^ \\/:*?"<>|]+([ ]+[^ \\/:*?"<>|]+)*)+$/.test(value);
  }

  return /^([a-zA-Z]:)(\\[^ \\/:*?"<>|]+([ ]+[^ \\/:*?"<>|]+)*)*\\?$/.test(value);
}

/**
 * Validate Windows paths containing environment variables.
 *
 * @param value
 * @param isFileOrProcess specify if the path parameter refers a file/process or folder path
 * @return boolean
 */
function validateWindowsSpecialPath(value: string, isFileOrProcess: boolean): boolean {
  const nonExtendableWindowsPaths = ['All Drives', 'Local Drives', 'Removable Drives'];

  if (nonExtendableWindowsPaths.includes(value)) {
    return true;
  }

  const dynamicWindowsPaths = [
    '%ALLUSERSPROFILE%',
    '%AppData%',
    '%LOCALAPPDATA%',
    '%ProgramFiles%',
    '%USERPROFILE%',
    '%CommonProgramFiles%',
    '%COMMONPROGRAMFILES\\(X86\\)%',
    '%WinDir%',
    '%PROGRAMFILES\\(X86\\)%',
    '%TEMP%',
    '%SystemDrive%',
  ];

  const dynamicPathsRegex = new RegExp('^(' + dynamicWindowsPaths.join('|') + ')', 'ig');
  const matches = value.match(dynamicPathsRegex);

  if (matches) {
    if (value.length === matches[0].length) {
      return !isFileOrProcess;
    }

    const relativePath = value.substring(matches[0].length);

    return validateWindowsAbsolutePath('C:' + relativePath, isFileOrProcess);
  }

  return false;
}

/**
 * Validate Unix paths containing environment variables or ~.
 *
 * @param value
 * @param isFileOrProcess specify if the path parameter refers a file/process or folder path
 * @return boolean
 */
function validateUnixSpecialPath(value: string, isFileOrProcess: boolean): boolean {
  if (value[0] === '~') {
    value = value.substring(1);
    return value !== '' && validateUnixAbsolutePath(value, isFileOrProcess);
  }

  const unixEnvironmentVariables = ['$HOME'];

  for (const envVar of unixEnvironmentVariables) {
    if (new RegExp('^' + envVar + '/?$').test(value)) {
      return false;
    }

    if (value.startsWith(envVar)) {
      const relativePath = value.substr(envVar.length);

      return validateUnixAbsolutePath(relativePath, isFileOrProcess);
    }
  }

  return false;
}

/**
 * Validate Unix absolute paths.
 *
 * @param value
 * @param isFileOrProcess specify if the path parameter refers a file/process or folder path
 * @return boolean
 */
function validateUnixAbsolutePath(value: string, isFileOrProcess: boolean): boolean {
  if (isFileOrProcess) {
    return !/(\0|^[^\/]|^\/*$|\/$)/.test(value);
  }

  return !/(\0|^[^\/])/.test(value);
}

/**
 * Validate network paths.
 *
 * @param value
 * @return boolean
 */
function validateNetworkPath(value: string): boolean {
  if (!value.startsWith('\\\\')) {
    return false;
  }

  const newPath = value.substr(2);
  const hostnameDelimiterPosition = newPath.indexOf('\\');

  if (hostnameDelimiterPosition !== -1) {
    const hostname = newPath.substr(0, hostnameDelimiterPosition);

    if (!validateHostname(hostname)) {
      return false;
    }

    const hostPath = newPath.substr(hostnameDelimiterPosition);
    if (!validateWindowsAbsolutePath('C:' + hostPath, false)) {
      return false;
    }
  } else {
    if (!validateHostname(newPath)) {
      return false;
    }
  }

  return true;
}

/**
 * Validate a host
 *
 * @param hostname
 * @return boolean
 */
function validateHostname(hostname: string): boolean {
  if (validator.isIP(hostname)) {
    return true;
  }

  if (hostname[0] === '.' || hostname[0] === '_' || hostname[0] === '-') {
    return false;
  }

  // proceed with domain and hostname verifications
  return /^[a-z0-9._-]+$/i.test(hostname);
}

/**
 * Validate a list of SHA-256 hashes.
 *
 * @param hashes
 * @return boolean
 */
function validateMultipleHashes(hashes: string): boolean {
  const hashesList = hashes.split(', ');

  for (const hash of hashesList) {
    if (!validator.isHash(hash, 'sha256')) {
      return false;
    }
  }

  return true;
}

/**
 * Validate an IP address (IPv4 or IPv6) with(out) a mask.
 *
 * @param address
 * @return boolean
 */
function validateIpWithMask(address: string): boolean {
  const ipv6Regex =
    /^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:)(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*(\/\d+)?$/;
  const ipv4Regex =
    /^([1-9]?\d|1\d\d|2[0-4]\d|25[0-5])\.([1-9]?\d|1\d\d|2[0-4]\d|25[0-5])\.([1-9]?\d|1\d\d|2[0-4]\d|25[0-5])\.([1-9]?\d|1\d\d|2[0-4]\d|25[0-5])(\/([1-9]|[1-2][0-9]|3[0-2]))?$/;

  if (!ipv4Regex.test(address) && !ipv6Regex.test(address)) {
    return false;
  }

  const ipParts = address.split('/');

  if (ipParts.length === 2) {
    // check the mask
    return +ipParts[1] <= 128 && !(ipParts[1].startsWith('0') && ipParts[1].length > 1);
  }

  return true;
}

/**
 * Validate a file extension or a list of extensions.
 *
 * @param extensions
 * @return boolean
 */
function validateExtension(extensions: string): boolean {
  return /^\s*(\.?[^\\\/:*?"<>| ]+[;,] *)*\.?[^\\\/:*?"<>| ]+\s*$/.test(extensions);
}

/**
 * Validate a threat name against invalid wildcards.
 *
 * @param name
 * @return boolean
 */
function validateThreatName(name: string): boolean {
  return !/^\s*$/.test(name) && !containsInvalidWildcards(name) && !name.includes('**');
}

/**
 * Validate a single SHA-1 or SHA-256 hash.
 *
 * @param hash
 * @return boolean
 */
function validateHash(hash: string): boolean {
  return /^([a-f0-9]{40}([a-f0-9]{24})?)$/i.test(hash);
}
